import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getLink } from '../../apiServices/linkApi';
import { getExtFromFileName } from '../../util/UtilFunctions';
import { validateFileTypeRendability } from '../../util/FileTypeSupportCheck';
import { useNotification } from '@/hooks/useNotification';
import { useSession } from '@/hooks/useSession';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { LinkFileList } from './LinkFileList';
import { transformItems } from '@/lib/transform';
import type { NormalizedItem } from '@/components/itemList/types';
import ResourceViewer from '../resource/ResourceViewer';
import type { ResourceContent } from '../resource/ResourcePage';
import ItemListLoading from '../../components/itemListLoading/ItemListLoading';

type LoadStatus = 'initial' | 'loading' | 'success' | 'error';

export default function OutsideLinkPage() {
  const { id: linkId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { isAuthenticated, updateIsQuerying } = useSession();
  const [status, setStatus] = useState<LoadStatus>('initial');
  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  const [openedResourceId, setOpenedResourceId] = useState<string | undefined>();
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, [linkId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchLinks() {
    setStatus('loading');
    try {
      const response = await getLink(linkId!);
      setItems(
        (response as { response: { Items: Record<string, unknown>[] } }).response.Items,
      );
      setStatus('success');
    } catch {
      notify({ id: String(Date.now()), type: 'error', message: 'Could not find link' });
      setStatus('error');
    }
  }

  function handleExpiredClick() {
    navigate(isAuthenticated ? '/in/folder/root' : '/');
  }

  // Opened resource view
  if (openedResourceId && status === 'success') {
    const resource = items.find(
      (r) => (r.id as Record<string, string>)?.S === openedResourceId,
    );
    if (!resource) return null;
    const name = (resource.name as Record<string, string>)?.S || '';
    const fileExt = getExtFromFileName(name);
    const isRendable = validateFileTypeRendability(
      (resource.type as Record<string, string>)?.S,
      fileExt,
    );

    const resourceContent: ResourceContent = {
      id: (resource.id as Record<string, string>)?.S || '',
      type: (resource.type as Record<string, string>)?.S || '',
      name,
      signedUrl: (resource.signedUrl as Record<string, string>)?.S || '',
      mimeType: (resource.mimeType as Record<string, string>)?.S || '',
      size: Number((resource.size as Record<string, string>)?.N || 0),
      snapshotUrl: (resource.snapshotUrl as Record<string, string>)?.S,
    };

    return (
      <div>
        <div className="mb-2">
          <Button variant="ghost" onClick={() => setOpenedResourceId(undefined)}>
            Back
          </Button>
        </div>
        <div className="relative flex h-[70vh] items-center justify-center overflow-hidden rounded-lg bg-background">
          {isRendable ? (
            <ResourceViewer
              content={resourceContent}
              fileExt={fileExt}
              fullscreen={fullscreen}
              onCloseFullscreen={() => setFullscreen(false)}
            />
          ) : (
            <p className="text-sm text-muted-foreground">.{fileExt} files can't be previewed</p>
          )}
        </div>
        <p className="mt-2 text-center text-sm text-muted-foreground">{name}</p>
      </div>
    );
  }

  // List view
  if (status === 'loading') return <ItemListLoading amount={7} />;

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <h5>Try again</h5>
        <Button onClick={fetchLinks}>Try again</Button>
      </div>
    );
  }

  if (status === 'success' && items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <h2 className="text-xl font-semibold">Link has expired</h2>
        <Button onClick={handleExpiredClick}>Return home</Button>
      </div>
    );
  }

  if (status === 'success') {
    const normalizedItems = transformItems(items) as NormalizedItem[];
    return (
      <LinkFileList
        items={normalizedItems}
        onRefresh={fetchLinks}
        updateIsQuerying={updateIsQuerying}
        onClickItem={(item) => setOpenedResourceId(item.id)}
      />
    );
  }

  return null;
}
